import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import PDFViewer from '@/components/PDFViewer';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle } from 'lucide-react';

const Gallery = () => {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Gallery", path: "/gallery" }
  ];

  const examples = [
    {
      id: 1,
      title: "Residential Property - First Floor",
      beforeImages: [
        {
          url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/hto61qm2_Example%201%20sketch.pdf",
          alt: "Hand-drawn floor plan sketch - Example 1",
          type: "pdf",
          rotation: 180  // Rotate 180 degrees (upside down fix)
        }
      ],
      afterImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/ot5fw0k5_Example%201%20Completed.pdf",
        alt: "Professional CAD floor plan - Example 1",
        type: "pdf"
      },
      description: "Converted from hand-drawn sketch to professional CAD floor plan with accurate measurements, clear room labels, and planning-ready output."
    },
    {
      id: 2,
      title: "Family Home - Ground Floor Layout",
      beforeImages: [
        {
          url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/irjnp6u7_Example%202%20Sketch%20a.JPG",
          alt: "Hand-drawn floor plan sketch - Example 2a",
          type: "image"
        },
        {
          url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/jte57zl8_Example%202%20sketch%20b.JPG",
          alt: "Hand-drawn floor plan sketch - Example 2b",
          type: "image"
        }
      ],
      afterImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/a077anxi_Example%202%20Completed.pdf",
        alt: "Professional CAD floor plan - Example 2",
        type: "pdf",
        rotation: 270  // Rotate 90 degrees counter-clockwise
      },
      description: "Multiple hand-drawn pages combined into a single professional floor plan with precise dimensions and professional presentation."
    },
    {
      id: 3,
      title: "Leasehold Property Plan",
      beforeImages: [
        {
          url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/ox7djran_Example%203%20sketch%20a.JPG",
          alt: "Hand-drawn floor plan sketch - Example 3a",
          type: "image"
        },
        {
          url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/8jn5ss77_Example%203%20sketch%20b.JPG",
          alt: "Hand-drawn floor plan sketch - Example 3b",
          type: "image"
        },
        {
          url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/y9ys3as0_Example%203%20sketch%20c.JPG",
          alt: "Hand-drawn floor plan sketch - Example 3c",
          type: "image"
        }
      ],
      afterImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/iifmqtro_Example%203%20leasehold%20plan%20completed.pdf",
        alt: "Professional leasehold CAD plan - Example 3",
        type: "pdf",
        rotation: 270  // Rotate 90 degrees counter-clockwise
      },
      description: "Leasehold property conversion from multiple sketch pages into professional CAD plan suitable for legal documentation and planning purposes."
    },
    {
      id: 4,
      title: "Complete Package - Multiple Output Formats",
      beforeImages: [
        {
          url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/l7rmhd52_Example%204%20sketch.JPG",
          alt: "Hand-drawn floor plan sketch - Example 4",
          type: "image"
        }
      ],
      afterImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/ixt1i3oe_Example%204%203d%20colour%20completed.jpg",
        alt: "Professional 3D CAD floor plan with color - Example 4",
        type: "image"
      },
      additionalFormats: [
        {
          url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/z9jyawtk_Example%204%202d%20B%20and%20W%20completed.pdf",
          label: "2D Black & White Plan",
          type: "pdf"
        },
        {
          url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/jammhfu0_Example%204%202d%20colour%20completed.pdf",
          label: "2D Color Plan",
          type: "pdf"
        },
        {
          url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/7wqg1pm8_Example%204%20to%20scale%20completed.pdf",
          label: "To Scale Version",
          type: "pdf"
        }
      ],
      description: "Single sketch transformed into multiple professional formats: 2D black & white, 2D color, 3D visualization, and to-scale versions. Shows the range of optional extras available."
    },
    {
      id: 5,
      title: "License Plan - Commercial Property",
      beforeImages: [
        {
          url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/wur75ec4_Example%205%20conversion%20and%20update%20sketch.JPG",
          alt: "Hand-drawn sketch for license application - Example 5",
          type: "image"
        }
      ],
      afterImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/rr4ftk7v_Example%205%20Licence%20plan%20completed.pdf",
        alt: "Professional license plan - Example 5",
        type: "pdf",
        rotation: 270  // Rotate 90 degrees counter-clockwise
      },
      description: "License plan conversion for commercial property. Hand-drawn sketch converted into professional CAD plan suitable for license applications and regulatory submissions."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        pageId="gallery"
        defaultTitle="Floor Plan Examples | House Plan Direct"
        defaultDescription="See real examples of hand sketches converted to professional CAD plans. Old house plans modernised. Planning and building control use cases. Before and after comparisons."
        aiSummary="Gallery showcasing real before-and-after examples of hand-drawn sketches and old plans converted into professional CAD drawings. Includes residential floor plans, leasehold plans, and 3D visualisations. Demonstrates quality suitable for planning permission and building control."
        keywords="sketch to CAD examples, old plans modernised, hand sketch conversion examples, planning drawings examples, CAD floor plan samples"
      />
      <SeoBreadcrumbs items={breadcrumbs} />
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Before & After Examples
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See how we transform DIY hand-drawn sketches into professional CAD floor plans. 
            Every conversion maintains accuracy while delivering planning-ready, professional results.
          </p>
        </div>

        {/* Value Proposition */}
        <div className="bg-blue-50 rounded-xl p-8 mb-16">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Accurate Measurements</h3>
              <p className="text-sm text-gray-600">
                Your dimensions translated precisely into professional CAD format
              </p>
            </div>
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Clear & Professional</h3>
              <p className="text-sm text-gray-600">
                Clean layouts suitable for planning applications and professional use
              </p>
            </div>
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Multiple Formats</h3>
              <p className="text-sm text-gray-600">
                Receive your plans in PDF, JPEG, and editable DWG formats
              </p>
            </div>
          </div>
        </div>

        {/* Examples */}
        <div className="space-y-20">
          {examples.map((example, index) => (
            <div key={example.id} className="border-b border-gray-200 pb-20 last:border-0">
              {/* Example Header */}
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Example {index + 1}: {example.title}
                </h2>
                <p className="text-lg text-gray-600">{example.description}</p>
              </div>

              {/* Before & After Layout */}
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Before - Input */}
                <div>
                  <div className="bg-red-50 text-red-800 font-semibold px-4 py-2 rounded-t-lg inline-block">
                    BEFORE - Your Sketch
                  </div>
                  <div className="border-4 border-red-200 rounded-b-lg rounded-tr-lg p-6 bg-gray-50">
                    {example.beforeImages.map((before, idx) => (
                      <div key={before.url || `${example.id}-before-${idx}`} className="mb-4 last:mb-0">
                        {before.type === 'image' ? (
                          <img 
                            src={before.url} 
                            alt={before.alt}
                            className="w-full h-auto rounded shadow-lg"
                          />
                        ) : (
                          <PDFViewer url={before.url} title={before.alt} rotation={before.rotation || 0} />
                        )}
                        {example.beforeImages.length > 1 && (
                          <p className="text-sm text-gray-600 mt-2 text-center">
                            Page {idx + 1} of {example.beforeImages.length}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* After - Output */}
                <div>
                  <div className="bg-green-50 text-green-800 font-semibold px-4 py-2 rounded-t-lg inline-block">
                    AFTER - Professional CAD Plan
                  </div>
                  <div className="border-4 border-green-200 rounded-b-lg rounded-tr-lg p-6 bg-gray-50">
                    <div className="bg-white rounded shadow-lg">
                      {example.afterImage.type === 'image' ? (
                        <div className="p-8">
                          <img 
                            src={example.afterImage.url} 
                            alt={example.afterImage.alt}
                            className="w-full h-auto rounded shadow-lg mb-4"
                          />
                          <p className="text-sm text-gray-600 text-center">3D Color Visualization</p>
                        </div>
                      ) : (
                        <PDFViewer url={example.afterImage.url} title={example.afterImage.alt} rotation={example.afterImage.rotation || 0} />
                      )}
                      
                      {/* Additional Formats */}
                      {example.additionalFormats && example.additionalFormats.length > 0 && (
                        <div className="p-6 border-t border-gray-200">
                          <p className="text-sm text-gray-600 mb-3 font-semibold">Also includes:</p>
                          <div className="space-y-2">
                            {example.additionalFormats.map((format) => (
                              <a
                                key={format.url}
                                href={format.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block text-sm text-blue-600 hover:underline"
                              >
                                → {format.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="p-6 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-3">Standard Includes:</p>
                        <ul className="text-sm text-left space-y-2">
                          <li className="flex items-start">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                            <span>Accurate measurements and dimensions</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                            <span>Clear room labels and annotations</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                            <span>Planning-ready professional format</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                            <span>PDF, JPEG, and DWG formats</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Convert Your Sketch?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Fixed price from £40. Fast turnaround. Professional results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/order">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                Start Your Conversion
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-700">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Gallery;
